/** One Server-Sent Event: the `event:` name (default "message") and the joined `data:` lines. */
export interface SseEvent {
  event: string;
  data: string;
}

/**
 * Incremental parser for a text/event-stream body. Feed it decoded chunks in any split;
 * it returns the events completed by each chunk. Handles \n, \r\n and \r line endings,
 * "field:value" with or without the space after the colon, comments and multi-line data.
 */
export class SseParser {
  private buffer = '';
  private eventName = '';
  private dataLines: string[] = [];

  push(chunk: string): SseEvent[] {
    this.buffer += chunk;
    const out: SseEvent[] = [];
    let idx: number;
    while ((idx = this.nextLineBreak()) >= 0) {
      const line = this.buffer.slice(0, idx);
      let skip = 1;
      if (this.buffer[idx] === '\r' && this.buffer[idx + 1] === '\n') {
        skip = 2;
      } else if (this.buffer[idx] === '\r' && idx + 1 === this.buffer.length) {
        // A lone \r at the end may be the first half of \r\n: wait for the next chunk.
        break;
      }
      this.buffer = this.buffer.slice(idx + skip);
      const ev = this.line(line);
      if (ev) {
        out.push(ev);
      }
    }
    return out;
  }

  /** Call at end of stream: dispatches a last event that had no trailing blank line. */
  flush(): SseEvent[] {
    const out: SseEvent[] = [];
    if (this.buffer.length > 0) {
      const ev = this.line(this.buffer.replace(/\r$/, ''));
      this.buffer = '';
      if (ev) {
        out.push(ev);
      }
    }
    const last = this.dispatch();
    if (last) {
      out.push(last);
    }
    return out;
  }

  private nextLineBreak(): number {
    const n = this.buffer.indexOf('\n');
    const r = this.buffer.indexOf('\r');
    if (n < 0) return r;
    if (r < 0) return n;
    return Math.min(n, r);
  }

  private line(line: string): SseEvent | null {
    if (line === '') {
      return this.dispatch();
    }
    if (line.startsWith(':')) {
      return null;
    }
    const colon = line.indexOf(':');
    const field = colon < 0 ? line : line.slice(0, colon);
    let value = colon < 0 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) {
      value = value.slice(1);
    }
    if (field === 'event') {
      this.eventName = value;
    } else if (field === 'data') {
      this.dataLines.push(value);
    }
    return null;
  }

  private dispatch(): SseEvent | null {
    if (this.dataLines.length === 0 && this.eventName === '') {
      return null;
    }
    const ev: SseEvent = { event: this.eventName || 'message', data: this.dataLines.join('\n') };
    this.eventName = '';
    this.dataLines = [];
    return ev;
  }
}

/** Reads a fetch() body stream to the end, calling onEvent for every SSE event. */
export async function readSseStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: SseEvent) => void
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  const parser = new SseParser();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      parser.push(decoder.decode(value, { stream: true })).forEach(onEvent);
    }
    const tail = decoder.decode();
    if (tail) {
      parser.push(tail).forEach(onEvent);
    }
    parser.flush().forEach(onEvent);
  } finally {
    reader.releaseLock();
  }
}
